/* eslint-disable no-nested-ternary */
/* eslint-disable function-paren-newline */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/indent */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { GetCanonicalURL } from '@helpers/environment';
import { useRouter } from 'next/router';
import HotelDetails from '@components/HotelDetails/HotelDetails';
import GraphiteRelatedHotels from '@components/GraphiteRelatedHotels/GraphiteRelatedHotels';
import SwiperCarousel from '@components/common/SwiperCarousel/SwiperCarousel';
import { getHotelDetailsV2, getProductDetails, getYotpoRatings } from '@utils/services';
import HotelDetailsType from '@customTypes/hotel-details';
import { ProductDetails } from '@customTypes/product-details';
import Slide from '@customTypes/slide';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import { useCookies } from 'react-cookie';
import useIsMobile from '@hooks/useIsMobile';
import ImageGallery from '@components/common/ImageGallery/ImageGallery';
import { dlpPageView, viewItemList } from '@events/DLP';
import actionTypes from '@context/actionTypes';
import { store } from '@context/store';
import { getMonthAndYear } from '@helpers/date';
import Image from 'next/image';
import { hotelFavoriteTappedOnDLP } from '@events/myFavorites';
import grayHeartIcon from '@assets/images/gray-heart-mobile-view-icon.svg';
import redHeartIcon from '@assets/images/red-heart-mobile-view-icon.svg';
import updateHotelFavorites from '@helpers/updateHotelFavorites';
import FavoritesToastMessage from '@components/MyFavorites/ToastMessage/FavoritesToastMessage';
import ProductBanner from '@components/ProductDetails/ProductDetailsVariant/Components/ProductBanner';
import MobileHotelDetails from '@components/HotelDetails/MobileHotelDetails';
import useFavorites from '@hooks/useFavorites';
import { VIEW_DETAIL_LISTING_PAGE } from '@constants/amplitudeEvents';
import { useEvents } from '@events/EventsProvider';
import useAmplitudePayloads from '@hooks/useAmplitudePayloads';
import { RelatedHotels, GetRelatedHotels } from '@utils/Graphite/graphite';
import { indoorPoolTitleTagsMap } from '@constants/customTitleTagMap';
import Reviews from '@components/common/Yotpo/Reviews';
import REVIEWS_THRESHOLD_TO_BE_SHOWN from '@constants/yotpo';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
} from '@constants/amplitudeExperiments';
import { getExperimentValue } from '@utils/amplitudeSSR';
import type { ExperimentType, ExperimentTypeABC } from '@customTypes/context';
import useOnScreen from '@hooks/useOnScreen';
import useSendAmplitudeExposureV2 from '@hooks/useSendAmplitudeExposureV2';
import useSendAmplitudeExposure from '@hooks/useSendAmplitudeExposure';
import { uniqBy } from 'lodash';
import SwiperCore from 'swiper';
import { ReferralCampaignFeatureFlagType } from '@context/FeatureFlagContext';

type BrowseHotelsPageProps = {
  canonicalURL: string;
  relatedHotels: Array<RelatedHotels>;
  hotelDetails: HotelDetailsType;
  initialProductDetails: ProductDetails;
  hotelId: number;
  referer: any;
  hasQueryParams: boolean;
  averageScore: number;
  totalReviews: number;
  productCategory: string;
  pricingMarkdownExperiment: ExperimentType;
  fixedDateSelectorExperiment: ExperimentType;
  ctasUpdateExperiment: ExperimentTypeABC;
  hotelImages: Slide[];
  initialGallerySlides: Slide[];
};

export async function getServerSideProps(context: any) {
  const {
    params,
    query,
    req: {
      headers: { referer },
    },
  } = context;

  const { slug, product_category: initialProductCategory, ...rest } = query;

  if (params.slug === 'inject.js.map') return { notFound: true };

  const hotelDetailsPromise = getHotelDetailsV2(params.slug);
  const hotelProductsPromise = hotelDetailsPromise.then((hotelDetails) =>
    getProductDetails(hotelDetails.id),
  );
  const relatedHotelsPromise = hotelDetailsPromise.then(
    (hotelDetails) => GetRelatedHotels(hotelDetails.url), // Graphite related hotels
  );

  const yotpoRatingsPromise = hotelProductsPromise.then((hotelProducts) => {
    const productId =
      hotelProducts?.products[0]?.product_id ??
      hotelProducts?.products[0]?.children?.[0]?.product_id;

    return getYotpoRatings(productId!);
  });

  try {
    const [hotelDetailsResult, hotelProductsResult, relatedHotelsResult, yotpoRatingsResult] =
      await Promise.allSettled([
        hotelDetailsPromise,
        hotelProductsPromise,
        relatedHotelsPromise,
        yotpoRatingsPromise,
      ]);

    if (hotelDetailsResult.status === 'rejected') {
      if (hotelDetailsResult.reason?.status === 404) {
        console.log(
          'Hotel Details API failed with a 404 error',
          hotelDetailsResult.reason.config.url,
        );
      } else {
        console.log('Hotel Details API failed', hotelDetailsResult.reason);
      }

      return {
        redirect: {
          destination: '/hotel-day-passes',
        },
      };
    }

    const hotelDetails = hotelDetailsResult.value;
    const hotelProducts =
      hotelProductsResult.status === 'fulfilled' ? hotelProductsResult.value : null;
    const relatedHotels =
      relatedHotelsResult.status === 'fulfilled' ? relatedHotelsResult.value : [];
    const yotpoRatings =
      yotpoRatingsResult.status === 'fulfilled'
        ? yotpoRatingsResult.value
        : { averageScore: 0, totalReviews: 0 };

    if (params.slug !== hotelDetails.url) {
      console.log('Hotel URL & Param URL mismatch', `${params.slug} | ${hotelDetails.url}`);

      return {
        redirect: {
          destination: `/hotels/${hotelDetails.url}`,
        },
      };
    }

    if (hotelDetails.active || query.preview === 'true') {
      const pricingMarkdownExperiment = await getExperimentValue<
        typeof AMPLITUDE_EXPERIMENT_VARIANT_NAME | typeof AMPLITUDE_EXPERIMENT_CONTROL_NAME | null
      >(context, 'stp-weekday-rate');

      const fixedDateSelectorExperiment = await getExperimentValue<
        typeof AMPLITUDE_EXPERIMENT_VARIANT_NAME | typeof AMPLITUDE_EXPERIMENT_CONTROL_NAME | null
      >(context, 'fixed-date-selector');

      const [ctasUpdateExperiment, referralCampaign, hotelOffersExperiment] = await Promise.all([
        getExperimentValue<ExperimentTypeABC>(context, 'ctas-update').catch(() => null),
        getExperimentValue<ReferralCampaignFeatureFlagType>(context, 'referral-campaign').catch(
          () => null,
        ),
        getExperimentValue<'on' | null>(context, 'hotel-offers').catch(() => null),
      ]);

      const hotelImages = hotelDetails.image.map((image) => ({
        key: image.picture.details.url,
        url: image.picture.details.url,
        blurDataUrl: image.picture.details.url,
        imgClassnames: 'w-full swiper-lazy',
      }));

      let productImages: {
        key: string;
        url: string;
        blurDataUrl: string;
        imgClassnames: string;
      }[] = [];

      if (
        initialProductCategory &&
        initialProductCategory.toLowerCase() !== 'all' &&
        hotelProducts
      ) {
        const productImageUrl = hotelProducts.products
          .filter(
            (p) =>
              p.product_images?.length > 0 &&
              p.product_categories?.some(
                (cat) => cat.name.toLowerCase() === initialProductCategory.toLowerCase(),
              ),
          )
          .flatMap((product) => product.product_images?.map((img) => img.picture?.url) || []);

        // Convert product images into the same format
        productImages = productImageUrl.map((url) => ({
          key: url,
          url,
          blurDataUrl: url,
          imgClassnames: 'w-full swiper-lazy',
        }));
      }

      const imageGallerySlides = uniqBy([...productImages, ...hotelImages], 'url');

      return {
        props: {
          canonicalURL: context.req.url ? GetCanonicalURL(context.req.url) : '',
          hotelDetails,
          hotelId: hotelDetails.id,
          hasQueryParams: Object.keys(rest).length > 0,
          initialProductDetails: hotelProducts,
          referer: referer || null,
          relatedHotels,
          averageScore: yotpoRatings.averageScore,
          totalReviews: yotpoRatings.totalReviews,
          pricingMarkdownExperiment,
          fixedDateSelectorExperiment,
          ctasUpdateExperiment,
          referralCampaign,
          productCategory: initialProductCategory || null,
          hotelImages,
          initialGallerySlides: imageGallerySlides,
          hotelOffersExperiment,
        },
      };
    }

    console.log('Hotel is not active', `${hotelDetails.name} | ${hotelDetails.url}`);
    return {
      redirect: {
        destination: `/hotel-day-passes?city_id=${hotelDetails.city_id}`,
      },
    };
  } catch (error: any) {
    console.log(error);

    return {
      redirect: {
        destination: '/hotel-day-passes',
      },
    };
  }
}

export default function BrowseHotels({
  canonicalURL,
  hotelDetails,
  hotelId,
  referer,
  relatedHotels,
  initialProductDetails,
  hasQueryParams,
  averageScore,
  totalReviews,
  pricingMarkdownExperiment,
  fixedDateSelectorExperiment,
  productCategory,
  hotelImages,
  initialGallerySlides,
  ctasUpdateExperiment,
}: BrowseHotelsPageProps) {
  const [galleryImages, setGalleryImages] = useState<Slide[]>(initialGallerySlides);
  const [productDetails, setProductDetails] = useState<ProductDetails | undefined>(
    initialProductDetails,
  );
  const [pageViewEventFired, setPageViewEventFired] = useState<boolean>(false);
  const [showImageGallery, setShowImageGallery] = useState<boolean>(false);
  const [renderImageGallery, setRenderImageGallery] = useState<boolean>(false);
  const [userFavoritesIds, setUserFavoritesIds] = useState<any>([]);
  const [showFavoriteToastMessage, setShowFavoriteToastMessage] = useState<boolean>(false);
  const [, setUserFavoritesMetadata] = useState<any>({});
  const [saveToFavorites, setSaveToFavorites] = useState<boolean>(false);
  const [amplitudePageViewEventSent, setAmplitudePageViewEventSent] = useState<boolean>(false);
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);

  const { amplitude, track } = useEvents();
  const { generateHotelPayload, generateProductPayload, getPercentageOfAvailableProducts } =
    useAmplitudePayloads();
  const router = useRouter();
  const isMobile = useIsMobile();
  const { date } = router.query;
  const urlDate =
    date &&
    date !== 'null' &&
    date !== 'undefined' &&
    !Number.isNaN(new Date(date.toString()).getTime())
      ? date.toString()
      : undefined;
  const { getItem, setItem } = useSessionStorage();
  const smartCalendarDate = getItem(sessionStorageKeys.SMART_CALENDAR_DATE);
  const sessionID = getItem(sessionStorageKeys.SESSION_ID);
  const [cookies, setCookie] = useCookies();
  const globalState = useContext(store);
  const { dispatch } = globalState;
  const { addHotelToFavorites } = useFavorites();
  const [isProductsVisible, productSectionRef] = useOnScreen();
  const [isReviewsVisible, scrollRef] = useOnScreen();
  const productsRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const [hideReviews, setHideReviews] = useState<boolean>(false);
  const [hideReviewsHeader, setHideReviewsHeader] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);

  const scrollToReviews = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const updateGalleryImages = (category: string) => {
    let productImages: Slide[] = [];

    if (category && category.toLowerCase() !== 'all' && productDetails) {
      const productImageUrl = productDetails.products
        .filter(
          (p: any) =>
            p.product_images?.length > 0 &&
            p.product_categories?.some(
              (cat: any) => cat.name.toLowerCase() === category.toLowerCase(),
            ),
        )
        .flatMap(
          (product: any) => product.product_images?.map((img: any) => img.picture?.url) || [],
        );

      productImages = productImageUrl.map((url: string) => ({
        key: url,
        url,
        blurDataUrl: url,
        imgClassnames: 'w-full swiper-lazy',
      }));
    }

    // Combine and deduplicate images
    const updatedSlides = uniqBy([...productImages, ...hotelImages], 'url');

    setGalleryImages(updatedSlides);

    // Update Swiper after slides change
    if (swiperInstance) {
      swiperInstance.update();
      swiperInstance.lazy.load();
    }
  };

  // Callback to receive Swiper instance from child
  const handleSwiperInit = (swiper: SwiperCore) => {
    setSwiperInstance(swiper);
  };

  const handleAddingHotelIdToFavorites = () => {
    addHotelToFavorites(
      cookies,
      setCookie,
      hotelDetails,
      setShowFavoriteToastMessage,
      userFavoritesIds,
      setSaveToFavorites,
      setItem,
      getItem,
      setUserFavoritesIds,
    );

    if (!userFavoritesIds.includes(hotelDetails.id.toString())) {
      hotelFavoriteTappedOnDLP(hotelDetails.id, hotelDetails.name);
    }
  };

  const handleSetActiveTab = (tabIndex: number) => {
    const scrollFn = {
      0: () => window.scroll({ top: 0, behavior: 'smooth' }),
      1: () => productsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      2: () => reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    };
    scrollFn[tabIndex as keyof typeof scrollFn]();
  };

  const handleScroll = useCallback(() => {
    if (productsRef.current && reviewsRef.current) {
      if (reviewsRef.current?.getBoundingClientRect().y < 20 || isReviewsVisible) {
        setActiveTab(2);
      } else if (productsRef.current?.getBoundingClientRect().y < 20 || isProductsVisible) {
        setActiveTab(1);
      } else {
        setActiveTab(0);
      }
    }
  }, [isProductsVisible, isReviewsVisible]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (userFavoritesIds.length === 0 && window.location.href.includes('hotels')) {
      updateHotelFavorites(
        cookies,
        null,
        setUserFavoritesMetadata,
        getItem,
        setUserFavoritesIds,
        null,
        null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({
      type: actionTypes.UPDATE_REFERER,
      payload: referer,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (hotelId && !urlDate && !smartCalendarDate) {
          const productDetailsData = await getProductDetails(hotelId);
          setProductDetails(productDetailsData);
        }
      } catch (error) {
        // fail silently
      }
    })();
  }, [hotelId, urlDate, smartCalendarDate]);

  useEffect(() => {
    // Hack to reset swiper slides
    setTimeout(() => {
      const swiperParent: any = document.getElementById('explore-hotels-swiper');
      const swiperElement = swiperParent?.swiper;
      if (swiperElement) swiperElement.slideTo();
    }, 500);
  }, [hotelDetails]);

  useEffect(() => {
    if (productDetails && !amplitudePageViewEventSent && amplitude) {
      const hotelPropertyGroup = generateHotelPayload(hotelDetails, productDetails.products);

      track(VIEW_DETAIL_LISTING_PAGE, {
        ...hotelPropertyGroup,
        hotel_is_hot_spot: !!hotelDetails.hot_spot,
        percentage_products_available: getPercentageOfAvailableProducts(productDetails.products),
        products: productDetails.products.map((product) => ({
          ...generateProductPayload(product),
          has_offer_visible_on_dlp_hotel_card: !!product.product_offer,
          offer_types_present: product.product_offer,
        })),
        search_date: smartCalendarDate || urlDate || false,
        product_category: productCategory,
      });

      setAmplitudePageViewEventSent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetails, amplitudePageViewEventSent, amplitude, date, productCategory]);

  useEffect(() => {
    if (productDetails && !pageViewEventFired) {
      // Event
      dlpPageView(
        hotelDetails,
        productDetails,
        smartCalendarDate,
        cookies.userInformation,
        sessionID,
      );

      // Event
      viewItemList({ ...hotelDetails, id: hotelId }, productDetails.products);

      setPageViewEventFired(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cookies.userInformation,
    hotelDetails,
    hotelId,
    pageViewEventFired,
    productDetails,
    sessionID,
    smartCalendarDate,
  ]);

  useSendAmplitudeExposure({
    flagKey: 'fixed-date-selector',
    validation: !!fixedDateSelectorExperiment,
    variant: fixedDateSelectorExperiment,
  });

  useSendAmplitudeExposureV2({
    flagKey: 'stp-weekday-rate',
    validation: !!pricingMarkdownExperiment,
    variant: pricingMarkdownExperiment,
  });

  useSendAmplitudeExposureV2({
    flagKey: 'ctas-update',
    validation: !!ctasUpdateExperiment,
    variant: ctasUpdateExperiment,
  });

  useEffect(() => {
    if (pricingMarkdownExperiment) {
      dispatch({
        type: actionTypes.SET_PRICING_MARKDOWN_EXPERIMENT,
        payload: pricingMarkdownExperiment,
      });
    }
  }, [dispatch, pricingMarkdownExperiment]);

  useEffect(() => {
    if (fixedDateSelectorExperiment) {
      dispatch({
        type: actionTypes.SET_FIXED_DATE_SELECTOR_EXPERIMENT,
        payload: fixedDateSelectorExperiment,
      });
    }
  }, [dispatch, fixedDateSelectorExperiment]);

  const onSwiperImageClick = () => {
    if (isMobile) {
      setRenderImageGallery(true);
      setShowImageGallery(true);
    }
  };

  // eslint-disable-next-line wrap-iife
  const pageTitle = ((hd, titleMap) => {
    const defaultTitle = `${hotelDetails?.name}: Pool & Spa Day Pass ${hotelDetails?.city_name} | ResortPass`;
    if (hd && titleMap[hd.id.toString()]) {
      return titleMap[hd.id.toString()];
    }
    return defaultTitle;
  })(hotelDetails, indoorPoolTitleTagsMap);

  const productId =
    productDetails?.products[0]?.product_id ??
    productDetails?.products[0]?.children?.[0]?.product_id;

  const findBestPrice = () => {
    if (!productDetails || !productDetails.products || !productDetails?.products?.length) {
      return null;
    }

    // Initialize with a very high price
    let bestPrice = Number.MAX_SAFE_INTEGER;

    // Loop through each product
    productDetails.products.forEach((product) => {
      const productPrice = parseFloat(product.adult_price);

      // Check the product's price
      if (productPrice < bestPrice) {
        bestPrice = productPrice;
      }

      // Check each child product's price
      product.children?.forEach((child) => {
        const childPrice = parseFloat(child.adult_price);
        if (childPrice < bestPrice) {
          bestPrice = childPrice;
        }
      });
    });

    return bestPrice === Number.MAX_SAFE_INTEGER ? null : bestPrice;
  };

  const schema = {
    '@context': 'https://schema.org',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: averageScore,
      reviewCount: totalReviews,
      bestRating: 5,
      itemReviewed: {
        '@type': 'Hotel',
        name: hotelDetails.name,
        address: {
          '@type': 'PostalAddress',
          addressCountry: hotelDetails.country_code,
          addressLocality: hotelDetails.city_name,
          addressRegion: hotelDetails.state_name,
          streetAddress: hotelDetails.address,
        },
        priceRange: `Starting at $${findBestPrice()}`,
      },
    },
  };

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} key="title" />
        <meta name="description" content={hotelDetails?.short_description} />
        <meta property="og:image" content={hotelDetails?.image?.[0].picture.details.url} />
        <meta property="og:site_name" content="ResortPass.com" />
        {hasQueryParams && <meta name="robots" content="noindex" />}
        {totalReviews >= REVIEWS_THRESHOLD_TO_BE_SHOWN && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        )}

        {canonicalURL && <link rel="canonical" href={canonicalURL} />}
      </Head>

      <main>
        {showFavoriteToastMessage ? (
          <FavoritesToastMessage inMobileView saveToFavorites={saveToFavorites} />
        ) : null}

        {!isMobile && hotelDetails.hot_spot ? (
          <div className="bg-solar-surface-brand-ultramarine flex font-rp-basetica h-10 items-center justify-center leading-14px text-base text-white">
            Hot Spot Savings
          </div>
        ) : null}

        {hotelDetails && !showImageGallery && (
          <div className="flex flex-col">
            {galleryImages && galleryImages.length > 0 && (
              <div className="h-auto w-full relative">
                <div className="absolute cursor-pointer right-4 top-4 z-10 d:hidden">
                  <Image
                    src={
                      userFavoritesIds &&
                      userFavoritesIds.includes(hotelDetails.metadata.hotel_id.toString())
                        ? redHeartIcon
                        : grayHeartIcon
                    }
                    alt="heart"
                    onClick={handleAddingHotelIdToFavorites}
                  />
                </div>

                <SwiperCarousel
                  slides={galleryImages}
                  slidesPerView={3}
                  carouselClass="dlp-carousel"
                  spaceBetween={8}
                  alt={hotelDetails.name}
                  handleClick={onSwiperImageClick}
                  startingSlideIndex={1}
                  onSwiperInit={handleSwiperInit}
                />

                {/* HOT SPOT */}
                {hotelDetails.hot_spot ? (
                  <div className="bg-solar-surface-brand-ultramarine flex font-rp-basetica h-8 items-center leading-14px justify-center text-xs text-white d:hidden">
                    Hot Spot Savings
                  </div>
                ) : null}

                {isMobile && hotelDetails.closed_for_season ? (
                  <ProductBanner
                    type="unavailable"
                    content={`Closed for the Season${
                      hotelDetails.reopen_date
                        ? `. Opens ${getMonthAndYear(hotelDetails.reopen_date)}`
                        : ''
                    }`}
                  />
                ) : null}
              </div>
            )}

            <div className="hidden mx-auto font-rp-pn-regular w-full max-w-6xl gap-10 top-118px sticky z-10 d:block">
              <div className="flex border-b border-rp-gray-divider w-2/3 gap-10 bg-white pt-4">
                <button
                  type="button"
                  className={`pb-4 text-base font-rp-pn-semi-bold ${
                    activeTab === 0 && 'underline dlp-desktop-tabs'
                  }`}
                  onClick={() => handleSetActiveTab(0)}
                >
                  Overview
                </button>
                <button
                  type="button"
                  className={`pb-4 text-base font-rp-pn-semi-bold ${
                    activeTab === 1 && 'underline dlp-desktop-tabs'
                  }`}
                  onClick={() => handleSetActiveTab(1)}
                >
                  Products
                </button>
                <button
                  type="button"
                  className={`pb-4 text-base font-rp-pn-semi-bold ${
                    activeTab === 2 && 'underline dlp-desktop-tabs'
                  }`}
                  onClick={() => handleSetActiveTab(2)}
                >
                  Reviews
                </button>
              </div>
            </div>

            <section
              className="flex flex-col mx-auto w-full font-rp-pn-regular d:max-w-6xl dlp-common pt-6 d:pt-10"
              id="main-section"
            >
              {isMobile ? (
                <MobileHotelDetails
                  hotelDetails={hotelDetails}
                  productDetails={productDetails}
                  urlDate={urlDate}
                  setProductDetails={setProductDetails}
                  setHideReviews={setHideReviews}
                  hideReviews={hideReviews}
                  setHideReviewsHeader={setHideReviewsHeader}
                  hideReviewsHeader={hideReviewsHeader}
                  averageScore={averageScore}
                  totalReviews={totalReviews}
                  productCategory={productCategory}
                  handleCategoryChange={updateGalleryImages}
                />
              ) : (
                <HotelDetails
                  hotelDetails={hotelDetails}
                  productDetails={productDetails}
                  hotelId={hotelId}
                  urlDate={urlDate}
                  setProductDetails={setProductDetails}
                  scrollToReviews={scrollToReviews}
                  setHideReviews={setHideReviews}
                  setHideReviewsHeader={setHideReviewsHeader}
                  averageScore={averageScore}
                  totalReviews={totalReviews}
                  productCategory={productCategory}
                  productsRef={productsRef}
                  productSectionRef={productSectionRef}
                  handleCategoryChange={updateGalleryImages}
                />
              )}
            </section>

            {relatedHotels.length ? (
              <section className="flex flex-col mx-auto w-full font-rp-pn-regular pt-6 mb-10 d:flex-row d:max-w-6xl d:pt-10 d:mb-0">
                <div className="w-full d:w-2/3">
                  <GraphiteRelatedHotels currentHotel={hotelDetails} hotels={relatedHotels} />
                </div>
              </section>
            ) : null}

            {!isMobile ? (
              <>
                <div
                  ref={reviewsRef}
                  className="w-full h-20 bg-gradient-to-b from-white to-rp-light-gray-2 d:h-40"
                />
                <div className="w-full h-14 -mt-12 left-0 bg-banner-wave bg-cover d:h-20 d:-mt-20" />
                <section
                  className="flex flex-col mx-auto w-full font-rp-pn-regular d:flex-row d:max-w-6xl dlp-common pt-6 d:pt-10 "
                  ref={scrollRef}
                >
                  <Reviews
                    productId={productId}
                    hideReviews={hideReviews}
                    hideReviewsHeader={hideReviewsHeader}
                  />
                </section>
              </>
            ) : null}
          </div>
        )}
      </main>

      <ImageGallery
        showImageGallery={showImageGallery}
        renderImageGallery={renderImageGallery}
        setShowImageGallery={setShowImageGallery}
        hotelDetails={hotelDetails}
        carouselSlides={galleryImages}
      />
    </>
  );
}
